import { useEffect, useMemo, useState } from 'react';
import { createHttpClient } from '@shared/api/httpClient';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { friendlyLabel, friendlyOptionLabel } from '@shared/utils/friendlyLabels';
import { statusPillClassName } from '@shared/utils/statusPill';
import type { JobsResponse, ReportJobRecord } from '@shared/types/api';
import { CMMC_LEVEL_OPTIONS, DEFAULT_GRID_LIMIT, GRID_LIMIT_OPTIONS, metadataFromResponse, type GridMetadata, type SearchMode } from './gridUi';

interface ReportSearchState {
  search: string;
  searchMode: SearchMode;
  status: string;
  cmmcLevel: string;
  limit: number;
}

const STATUS_OPTIONS = [
  '',
  'UPLOADED',
  'PROCESSING',
  'VALIDATING',
  'AUDITOR_PACKAGE_READY',
  'DOWNLOAD_LINK_SENT',
  'EVIDENCE_PACKAGE_UPLOADED',
  'PROCESSED_FINAL_PACKAGE_READY',
  'FAILED',
];

function formatDate(value?: string): string {
  return formatLocalDateTime(value);
}

function valueOrDash(value?: string | number | null): string {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

function rawReportStatus(job: ReportJobRecord): string {
  const hasFinalPackage = Boolean(job.finalAuditorPackageName || job.finalAuditorPackageS3Uri || job.finalPackageDeliveryStatus);
  const hasFinalReadiness = job.finalReadinessTotalControls !== undefined && job.finalReadinessTotalControls !== null;
  if (hasFinalPackage && hasFinalReadiness) return 'AUDITOR_PACKAGE_READY';
  return String(job.displayStatus || job.jobStatus || job.packageLifecycleStatus || job.status || '—');
}

function reportStatus(job: ReportJobRecord): string {
  return friendlyLabel(rawReportStatus(job));
}

function rawDeliveryStatus(job: ReportJobRecord): string {
  return String(job.finalPackageDeliveryStatus || '—');
}

function deliveryStatus(job: ReportJobRecord): string {
  return friendlyLabel(rawDeliveryStatus(job));
}

function sourceJobId(job: ReportJobRecord): string {
  const value = job.sourceJobId || job.parentJobId;
  if (!value || value === job.jobId) return '—';
  return String(value);
}

function readinessPercent(job: ReportJobRecord): string {
  if (job.readinessPercent !== undefined && job.readinessPercent !== null) {
    return `${job.readinessPercent}%`;
  }
  const passCount = Number(job.finalReadinessPassControls ?? 0);
  const notApplicable = Number(job.finalReadinessNotApplicableControls ?? 0);
  const total = Number(job.finalReadinessTotalControls ?? 0);
  if (!total) return '—';
  return `${Math.round(((passCount + notApplicable) / total) * 1000) / 10}%`;
}

function readinessSummary(job: ReportJobRecord): string {
  const total = job.finalReadinessTotalControls;
  const pass = job.finalReadinessPassControls;
  const partial = job.finalReadinessPartialControls;
  const fail = job.finalReadinessFailControls;
  const notApplicable = job.finalReadinessNotApplicableControls;

  if (total === undefined && pass === undefined && partial === undefined && fail === undefined && notApplicable === undefined) {
    return '—';
  }

  return `PASS ${pass ?? '—'} / PARTIAL ${partial ?? '—'} / FAIL ${fail ?? '—'} / N/A ${notApplicable ?? '—'} / TOTAL ${total ?? '—'}`;
}

function detailRow(label: string, value?: string | number | null) {
  return (
    <div className="detail-row" key={label}>
      <span>{label}</span>
      <strong>{valueOrDash(value)}</strong>
    </div>
  );
}

export default function ReportsPage() {
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const [data, setData] = useState<JobsResponse | null>(null);
  const [metadata, setMetadata] = useState<GridMetadata | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<ReportJobRecord | null>(null);
  const [filters, setFilters] = useState<ReportSearchState>({
    search: '',
    searchMode: 'contains',
    status: '',
    cmmcLevel: '',
    limit: DEFAULT_GRID_LIMIT,
  });

  async function loadReports(nextFilters = filters, pageNumber = page) {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<JobsResponse>('/jobs', {
        query: {
          category: 'reports',
          limit: nextFilters.limit,
          page: pageNumber,
          search: nextFilters.search || undefined,
          searchMode: nextFilters.searchMode,
          status: nextFilters.status || undefined,
          cmmcLevel: nextFilters.cmmcLevel || undefined,
        },
      });
      setData(response);
      setMetadata(metadataFromResponse(response));
      setPage(response.page ?? pageNumber);
      setSelectedReport(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load report status.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await api.get<JobsResponse>('/jobs', {
          query: { category: 'reports', limit: DEFAULT_GRID_LIMIT, page: 1, searchMode: 'contains' },
        });
        if (!cancelled) {
          setData(response);
          setMetadata(metadataFromResponse(response));
          setPage(response.page ?? 1);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load report status.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    let cancelled = false;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void api
        .get<JobsResponse>('/jobs', {
          query: {
            category: 'reports',
            limit: filters.limit,
            page,
            search: filters.search || undefined,
            searchMode: filters.searchMode,
            status: filters.status || undefined,
            cmmcLevel: filters.cmmcLevel || undefined,
          },
        })
        .then((response) => {
          if (cancelled) return;
          setData(response);
          setMetadata(metadataFromResponse(response));
          setPage(response.page ?? page);
        })
        .catch(() => {
          // Keep the last successful report view during transient polling errors.
        });
    }, 10_000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [api, filters, page]);

  const jobs = data?.jobs ?? [];

  return (
    <div className="page-stack">
      <div className="page-heading">
        <h2>Reports</h2>
        <p>View readiness summaries and final auditor package delivery status for your organization.</p>
      </div>

      <article className="card">
        <div className="section-title-row">
          <div>
            <h3>Report status</h3>
            <p className="muted-text">Latest report and final package records are shown first. Select a row to view report details.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => void loadReports()}>
            Refresh
          </button>
        </div>

        <form
          className="filter-grid"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            void loadReports(filters, 1);
          }}
        >
          <label>
            Search
            <input
              value={filters.search}
              onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
              placeholder="Report, job, status, or level"
            />
          </label>
          <label>
            Match type
            <select value={filters.searchMode} onChange={(event) => setFilters((current) => ({ ...current, searchMode: event.target.value as SearchMode }))}>
              <option value="contains">Contains</option>
              <option value="exact">Exact word</option>
              <option value="beginsWith">Begins with</option>
            </select>
          </label>
          <label>
            Status
            <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
              {STATUS_OPTIONS.map((status) => (
                <option key={status || 'all'} value={status}>{friendlyOptionLabel(status, 'All statuses')}</option>
              ))}
            </select>
          </label>
          <label>
            CMMC Level
            <select value={filters.cmmcLevel} onChange={(event) => setFilters((current) => ({ ...current, cmmcLevel: event.target.value }))}>
              {CMMC_LEVEL_OPTIONS.map((level) => (
                <option key={level || 'all'} value={level}>{level || 'All levels'}</option>
              ))}
            </select>
          </label>
          <label>
            Limit
            <select value={filters.limit} onChange={(event) => setFilters((current) => ({ ...current, limit: Number(event.target.value) }))}>
              {GRID_LIMIT_OPTIONS.map((limit) => (
                <option key={limit} value={limit}>{limit}</option>
              ))}
            </select>
          </label>
          <div className="form-actions filter-actions">
            <button className="primary-button" type="submit">Search</button>
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                const cleared: ReportSearchState = { search: '', searchMode: 'contains', status: '', cmmcLevel: '', limit: DEFAULT_GRID_LIMIT };
                setFilters(cleared);
                setPage(1);
                void loadReports(cleared, 1);
              }}
            >
              Clear
            </button>
          </div>
        </form>


        {metadata && !loading && !error && (
          <div className="grid-header-row compact-grid-header">
            <span className="result-count">Showing {jobs.length} of {metadata.matchedCount ?? jobs.length} matched · Page {metadata.page ?? page}{metadata.pageCount ? ` of ${metadata.pageCount}` : ''}</span>
            <div className="pagination-row">
              <button className="secondary-button" type="button" disabled={!metadata.hasPreviousPage || loading} onClick={() => void loadReports(filters, Math.max(1, page - 1))}>Previous</button>
              <button className="secondary-button" type="button" disabled={!metadata.hasNextPage || loading} onClick={() => void loadReports(filters, page + 1)}>Next</button>
            </div>
          </div>
        )}
        {loading && <p>Loading reports...</p>}
        {error && <p className="error-text">{error}</p>}
        {!loading && !error && jobs.length === 0 && <p>No report records are available for this client.</p>}
        {!loading && !error && jobs.length > 0 && (
          <div className="table-scroll">
            <table className="data-table interactive-table">
              <thead>
                <tr>
                  <th>Report / Job</th>
                  <th>Status</th>
                  <th>Delivery</th>
                  <th>CMMC</th>
                  <th>Readiness</th>
                  <th>Expires</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, index) => {
                  const rowKey = `${job.clientId || 'client'}-${job.jobId || index}`;
                  const selected = selectedReport?.jobId === job.jobId;
                  return (
                    <tr
                      key={rowKey}
                      className={selected ? 'selected-row' : undefined}
                      onClick={() => setSelectedReport(job)}
                    >
                      <td>
                        <div className="cell-primary">{valueOrDash(job.jobId)}</div>
                        {sourceJobId(job) !== '—' && (
                          <div className="cell-secondary">Source evidence job: {sourceJobId(job)}</div>
                        )}
                      </td>
                      <td><span className={statusPillClassName(rawReportStatus(job))}>{reportStatus(job)}</span></td>
                      <td><span className={statusPillClassName(rawDeliveryStatus(job))}>{deliveryStatus(job)}</span></td>
                      <td>{valueOrDash(job.cmmcLevel)}</td>
                      <td>{readinessPercent(job)}</td>
                      <td>{formatDate(job.finalAuditorPackageDownloadUrlExpiresAt)}</td>
                      <td>{formatDate(job.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </article>

      {selectedReport && (
        <article className="card detail-panel">
          <h3>Report details</h3>
          <p className="muted-text">This view intentionally excludes internal S3 locations, AWS account identifiers, pipeline/build identifiers, and SURELINC processing metadata.</p>
          <div className="details-grid">
            {detailRow('Report job ID', selectedReport.jobId)}
            {detailRow('Source evidence job ID', sourceJobId(selectedReport))}
            <div className="detail-row"><span>Status</span><strong><span className={statusPillClassName(rawReportStatus(selectedReport))}>{reportStatus(selectedReport)}</span></strong></div>
            <div className="detail-row"><span>Delivery status</span><strong><span className={statusPillClassName(rawDeliveryStatus(selectedReport))}>{deliveryStatus(selectedReport)}</span></strong></div>
            {detailRow('CMMC level', selectedReport.cmmcLevel)}
            {detailRow('Readiness percentage', readinessPercent(selectedReport))}
            {detailRow('Readiness summary', readinessSummary(selectedReport))}
            {detailRow('Download link expires', formatDate(selectedReport.finalAuditorPackageDownloadUrlExpiresAt))}
            {detailRow('Created', formatDate(selectedReport.createdAt))}
            {detailRow('Updated', formatDate(selectedReport.updatedAt))}
          </div>
        </article>
      )}
    </div>
  );
}
